import React, { useCallback, useState } from 'react';
import {
  IonContent,
  IonPage,
  useIonViewDidEnter,
  useIonViewWillEnter,
  useIonViewWillLeave,
} from '@ionic/react';
import './Default.css';
import AppPageHeader from '../components/AppPageHeader';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router';
import useEnhancedAuth from '../EnhancedAuth';
import { Farecard } from '../services/types/apitypes';
import CardInformation from '../usecases/farecard/CardInformation';
import { apiGetFareCards, apiUpdateFareCardNickname } from '../services/api';
import { getAppError, getAppSuccess } from '../utils/messages';
import { LoadingController } from '../utils/LoadingController';
import { useToaster } from '../hooks/useToaster';

const CardInformationPage: React.FC = () => {
  const auth = useEnhancedAuth();
  const [t] = useTranslation('global');
  const router = useHistory();
  const [present] = useToaster();

  const { cardid } = useParams<{ cardid: string }>();

  const [cardInfo, setCardInfo] = useState<Farecard | undefined>(undefined);
  const [isPageActive, setIsPageActive] = useState(false);

  useIonViewDidEnter(() => {
    setIsPageActive(true);
  });
  useIonViewWillLeave(() => {
    setIsPageActive(false);
  });

  useIonViewWillEnter(() => {
    cardid && auth && loadCardInformation();
  }, [cardid, auth]);

  const loadCardInformation = async () => {
    try {
      LoadingController.show();
      const result = await apiGetFareCards(auth);
      const card = result.data.find((c) => c.cardId === cardid);

      if (!card) {
        throw new Error(t('cardinformation.errorCardNotFound')!);
      }
      setCardInfo(card);
    } catch (error: { message?: string } | any) {
      present(getAppError(error?.message || t('cardinformation.errorCardNotFound')));
      router.push('/cards');
    } finally {
      LoadingController.hide();
    }
  };

  const onSave = useCallback(
    (nickname: string) => {
      if (nickname === cardInfo?.nickname) {
          return present(getAppError(t('cardinformation.errorNicknameUnchanged')));
      }

      LoadingController.show();
      apiUpdateFareCardNickname(cardid, nickname || '', auth)
        .then((response) => {
          setCardInfo(response.data);
          present(getAppSuccess(t('cardinformation.successNicknameUpdated')));
          router.push('/cards');
        })
        .catch((error: { message?: string } | any) => {
          present(
            getAppError(error?.message || t('cardinformation.errorUpdateNickname')),
          );
        })
        .finally(() => {
          LoadingController.hide();
        });
    },
    [cardid],
  );

  const goBack = () => {
    router.push('/card/' + cardid + '/options');
  };

  return (
    <IonPage id="farecardinformation-page">
      <AppPageHeader
        isPageActive={isPageActive}
        back={() => goBack()}
        title={t('cardinformation.title')}
      ></AppPageHeader>
      <IonContent fullscreen className="ion-padding default-content">
        <CardInformation card={cardInfo} onSubmit={onSave} />
      </IonContent>
    </IonPage>
  );
};

export default CardInformationPage;
